import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Check, Loader2, ArrowRight } from 'lucide-react';
import { ConsentCheckbox } from './shared/ConsentCheckbox';

import { trackConversion, trackMetaEvent, CONVERSIONS } from '../lib/gtag';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Geschätzter Newsletter-Signup-Wert für Meta/Google-Werbewert-Optimierung.
 * Meta/Google verlangen VARIIERENDE Werte — identische Beträge lösen die
 * Warnung "Preise sind für alle Events identisch" aus. Basiswert ~5–10 €
 * (realistische Predicted LTV eines Abonnenten × Conversion-Rate).
 */
function predictedNewsletterValue(source) {
  const bases = { section: 6.0, footer: 4.5, 'footer-hero': 7.5 };
  const base = bases[source] || 5.5;
  // ±20% deterministische Streuung basierend auf Timestamp → Wert variiert je Signup
  const jitter = 0.8 + (Date.now() % 400) / 1000; // 0.80–1.20
  return Math.round(base * jitter * 100) / 100;
}

/**
 * NewsletterSignup — Updates rund um die Autowelt.
 * variant: 'section' (Landing-Sektion) | 'footer' (kompakte Zeile)
 */
export default function NewsletterSignup({ variant = 'section' }) {
  const [email, setEmail] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [state, setState] = useState('idle'); // idle | loading | done
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === 'loading') return;
    if (!consentChecked) {
      setMessage('Bitte bestätige die Newsletter-Einwilligung.');
      return;
    }
    setState('loading');
    try {
      const { data } = await axios.post(`${API}/api/newsletter/subscribe`, { email, source: variant });
      axios.post(`${API}/api/consents`, { consent_type: 'newsletter', source: `newsletter-${variant}`, email }).catch(() => {});
      setMessage(data.message);
      setState('done');
      // Predicted LTV EINMAL berechnen, dann in beiden Events konsistent verwenden
      const subscribeValue = predictedNewsletterValue(variant);
      // Enhanced Conversions: E-Mail an Google Ads mitliefern (gtag hasht clientseitig).
      trackConversion(CONVERSIONS.NEWSLETTER, {
        value: subscribeValue,
        currency: 'EUR',
        userData: { email: email.trim(), country: 'DE' },
      });
      // Meta Subscribe: Wert muss variieren (nicht identisch für alle) + gültige Währung
      // → wir senden geschätzten CLV pro Signup-Quelle mit leichter Randomisierung.
      trackMetaEvent('Subscribe', {
        params: {
          value: subscribeValue,
          currency: 'EUR',
          predicted_ltv: subscribeValue,
          content_category: `newsletter-${variant}`,
        },
        email,
      });
    } catch {
      setMessage('Das hat nicht geklappt — bitte prüfe deine E-Mail-Adresse.');
      setState('idle');
    }
  };

  if (variant === 'footer-hero') {
    return (
      <div data-testid="newsletter-footer-hero">
        {state === 'done' ? (
          <div className="flex items-start gap-3 rounded-2xl bg-emerald-400/10 border border-emerald-400/30 p-5" data-testid="newsletter-hero-success">
            <Check className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
            <p className="text-sm text-emerald-100 leading-relaxed">{message}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-hero-form">
            <div className="relative flex-1">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" strokeWidth={2} />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="deine@email.de"
                className="w-full py-4 pl-11 pr-4 rounded-full bg-white/10 border border-white/15 text-sm text-white placeholder:text-stone-500 focus:outline-none focus:border-emerald-400/60"
                data-testid="newsletter-hero-input"
              />
            </div>
            <button
              type="submit"
              disabled={state === 'loading'}
              className="py-4 px-7 rounded-full bg-emerald-400 text-stone-950 text-sm font-semibold hover:bg-emerald-300 hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-neon"
              data-testid="newsletter-hero-submit"
            >
              {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Anmelden <ArrowRight className="w-4 h-4" strokeWidth={2} /></>}
            </button>
          </form>
        )}
        <div className="mt-3">
          <ConsentCheckbox type="newsletter" dark checked={consentChecked} onChange={setConsentChecked} testId="newsletter-hero-consent" />
          {message && state !== 'done' && <p className="text-[11px] text-red-400 mt-2" data-testid="newsletter-hero-error">{message}</p>}
        </div>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div data-testid="newsletter-footer">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-stone-400 mb-3">Auto-Updates</p>
        {state === 'done' ? (
          <p className="flex items-center gap-2 text-sm text-emerald-600" data-testid="newsletter-footer-success"><Check className="w-4 h-4" strokeWidth={2.5} /> {message}</p>
        ) : (
          <form onSubmit={submit} className="space-y-2">
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="deine@email.de"
                className="flex-1 min-w-0 h-10 px-3.5 rounded-full bg-white border border-stone-200 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-400"
                data-testid="newsletter-footer-input"
              />
              <button type="submit" disabled={!consentChecked} className="h-10 px-4 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-stone-700 transition-colors flex items-center disabled:opacity-40 disabled:cursor-not-allowed" data-testid="newsletter-footer-submit">
                {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" strokeWidth={2} />}
              </button>
            </div>
            <ConsentCheckbox type="newsletter" checked={consentChecked} onChange={setConsentChecked} testId="newsletter-footer-consent" />
            {message && <p className="text-[11px] text-red-500">{message}</p>}
          </form>
        )}
      </div>
    );
  }

  return (
    <section className="py-20 sm:py-24 bg-white" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="rounded-[2rem] bg-stone-950 relative overflow-hidden p-10 sm:p-14 lg:p-16">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(62,255,126,0.14),_transparent_55%)]" aria-hidden="true" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-emerald-300 mb-4">FuelX Newsletter</p>
              <h2 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl tracking-[-0.025em] leading-[1.0]">
                Updates rund um <span className="text-stone-400">die Autowelt.</span>
              </h2>
              <p className="text-sm sm:text-base text-stone-400 leading-relaxed mt-4 max-w-md">
                Spritpreis-Trends, neue Card-Vorteile, Hotel-Deals und Auto-News — kompakt per E-Mail, kein Spam, jederzeit abbestellbar.
              </p>
            </div>
            <div>
              {state === 'done' ? (
                <div className="flex items-start gap-3 rounded-2xl bg-emerald-400/10 border border-emerald-400/30 p-5" data-testid="newsletter-success">
                  <Check className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <p className="text-sm text-emerald-100 leading-relaxed">{message}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
                  <div className="relative flex-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" strokeWidth={2} />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="deine@email.de"
                      className="w-full h-13 py-4 pl-11 pr-4 rounded-full bg-white/10 border border-white/15 text-sm text-white placeholder:text-stone-500 focus:outline-none focus:border-emerald-400/60"
                      data-testid="newsletter-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={state === 'loading'}
                    className="h-13 py-4 px-7 rounded-full bg-white text-stone-900 text-sm font-medium hover:bg-stone-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="newsletter-submit"
                  >
                    {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Anmelden <ArrowRight className="w-4 h-4" strokeWidth={2} /></>}
                  </button>
                </form>
              )}
              <div className="mt-3">
                <ConsentCheckbox type="newsletter" dark checked={consentChecked} onChange={setConsentChecked} testId="newsletter-section-consent" />
                {message && state !== 'done' && <p className="text-[11px] text-red-400 mt-2" data-testid="newsletter-section-error">{message}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
